/**
 * Base implementation of the API client
 */
export class APIClientBase {
    baseUrl;
    authService;
    fetchImpl;
    /**
     * Create a new API client
     * @param baseUrl - The base URL for API requests
     * @param authService - The authentication service
     * @param fetchImpl - The fetch implementation to use
     */
    constructor(baseUrl, authService, fetchImpl) {
        this.baseUrl = baseUrl;
        this.authService = authService;
        this.fetchImpl = fetchImpl;
    }
    /**
     * Set the base URL for API requests
     * @param baseUrl - The base URL
     */
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
    }
    /**
     * Build a URL with query parameters
     * @param url - The base URL
     * @param params - The query parameters
     * @returns The full URL
     */
    buildUrl(url, params = {}) {
        const queryString = Object.entries(params)
            .filter((param) => param[0] && param[1] != null)
            .map((param) => `${param[0]}=${param[1]}`)
            .join("&");
        return queryString ? `${url}?${queryString}` : url;
    }
    /**
     * Get the authorization header
     * @returns Promise with the authorization header
     */
    async getAuthHeader() {
        // Get access token from auth service
        await this.authService.autoSignIn();
        const token = await this.authService.getAccessToken();
        return token ? { Authorization: `bearer ${token}` } : {};
    }
    /**
     * Create request options with authorization
     * @param method - The HTTP method
     * @param body - The request body
     * @returns Promise with the request options
     */
    async createRequestOptions(method, body) {
        const authHeader = await this.getAuthHeader();
        const options = {
            method,
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
                ...authHeader,
            },
        };
        if (body) {
            options.body = JSON.stringify(body);
        }
        return options;
    }
    /**
     * Handle API response for data extraction
     * @param response - The fetch response
     * @returns Promise with the parsed response data
     */
    async handleResponse(response) {
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const json = await response.json();
        return json.data;
    }
    /**
     * Handle API response for full response
     * @param response - The fetch response
     * @returns Promise with the full parsed response
     */
    async handleFullResponse(response) {
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return (await response.json());
    }
    /**
     * Perform a fetch request
     * @param url - The URL to request
     * @param options - The request options
     * @returns Promise with the fetch response
     */
    async fetchWithRetry(url, options) {
        try {
            return await this.fetchImpl(url, options);
        }
        catch (error) {
            console.error("Fetch error:", error);
            throw error;
        }
    }
    /**
     * Perform a GET request
     * @param url - The URL to request
     * @param params - Query parameters
     * @returns Promise with the response data
     */
    async get(url, params = {}) {
        const fullUrl = this.buildUrl(`${this.baseUrl}${url}`, params);
        const options = await this.createRequestOptions("GET");
        try {
            const response = await this.fetchWithRetry(fullUrl, options);
            return await this.handleResponse(response);
        }
        catch (error) {
            console.error("GET request error:", error);
            throw error;
        }
    }
    /**
     * Perform a GET request that returns a paginated response
     * @param url - The URL to request
     * @param params - Query parameters
     * @returns Promise with the full response
     */
    async getPage(url, params = {}) {
        const fullUrl = this.buildUrl(`${this.baseUrl}${url}`, params);
        const options = await this.createRequestOptions("GET");
        try {
            const response = await this.fetchWithRetry(fullUrl, options);
            return await this.handleFullResponse(response);
        }
        catch (error) {
            console.error("GET page request error:", error);
            throw error;
        }
    }
    /**
     * Perform a POST request
     * @param url - The URL to request
     * @param data - The data to send
     * @returns Promise with the response data
     */
    async post(url, data) {
        // Make sure we have a valid token
        await this.authService.autoSignIn();
        const fullUrl = `${this.baseUrl}${url}`;
        const options = await this.createRequestOptions("POST", data);
        try {
            const response = await this.fetchWithRetry(fullUrl, options);
            return await this.handleResponse(response);
        }
        catch (error) {
            console.error("POST request error:", error);
            throw error;
        }
    }
    /**
     * Perform a PUT request
     * @param url - The URL to request
     * @param data - The data to send
     * @returns Promise with the response data
     */
    async put(url, data) {
        // Make sure we have a valid token
        await this.authService.autoSignIn();
        const fullUrl = `${this.baseUrl}${url}`;
        const options = await this.createRequestOptions("PUT", data);
        try {
            const response = await this.fetchWithRetry(fullUrl, options);
            return await this.handleResponse(response);
        }
        catch (error) {
            console.error("PUT request error:", error);
            throw error;
        }
    }
}
